import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../lib/api";
import { AdminButton, AdminInput, AdminTextarea, AdminCard } from "../AdminUI";
import { Save, Loader2, Plus, Trash2, Snowflake } from "lucide-react";
import { toast } from "sonner";

const EMPTY = "__NONE__";
const MAX_SLOTS = 40;

const TakeHomeTab = () => {
    const [data, setData] = useState(null);
    const [flavors, setFlavors] = useState([]);
    const [slots, setSlots] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        Promise.all([
            api.get("/take-home"),
            api.get("/flavors"),
        ]).then(([t, f]) => {
            setData(t.data);
            setFlavors(f.data);
            const current = (t.data.flavors || []).map((v) => v || EMPTY);
            // start with at least 4 slots so users see something to fill
            while (current.length < 4) current.push(EMPTY);
            setSlots(current);
        });
    }, []);

    if (!data) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-garden-green" /></div>;

    const setSlot = (i, v) => {
        const next = [...slots];
        next[i] = v;
        setSlots(next);
    };
    const addSlot = () => { if (slots.length < MAX_SLOTS) setSlots([...slots, EMPTY]); };
    const removeSlot = (i) => setSlots(slots.filter((_, idx) => idx !== i));

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                ...data,
                flavors: slots.filter((s) => s !== EMPTY),
            };
            const res = await api.put("/take-home", payload);
            setData(res.data);
            toast.success("Für Später gespeichert");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6 max-w-4xl" data-testid="take-home-tab">
            {flavors.length === 0 && (
                <div className="bg-pastel-pink/30 border border-pastel-pink rounded-2xl p-4 text-sm text-deep-green">
                    Sie haben noch keine Sorten angelegt. Legen Sie zuerst im Tab <Link to="#" className="font-semibold underline">"Sorten"</Link> Ihre Eissorten an.
                </div>
            )}

            <AdminCard className="space-y-4">
                <h3 className="font-serif text-xl text-deep-green">Einleitung</h3>
                <AdminInput label="Überschrift" value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} data-testid="take-home-title" />
                <AdminTextarea label="Text" rows={4} value={data.intro_text} onChange={(e) => setData({ ...data, intro_text: e.target.value })} data-testid="take-home-text" />
            </AdminCard>

            <AdminCard className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                        <h3 className="font-serif text-xl text-deep-green">Verfügbare Sorten im Froster</h3>
                        <p className="text-sm text-deep-green/65 mt-0.5">
                            Bis zu {MAX_SLOTS} Sorten auswählbar. Leere Plätze werden auf der Webseite nicht angezeigt.
                        </p>
                    </div>
                    <span className="text-sm text-deep-green/70 font-medium">{slots.length} / {MAX_SLOTS}</span>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {slots.map((slot, i) => (
                        <div key={i} className="flex items-center gap-2" data-testid={`takehome-slot-${i}`}>
                            <div className="relative flex-1">
                                <Snowflake className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-garden-green-dark pointer-events-none" />
                                <select
                                    value={slot}
                                    onChange={(e) => setSlot(i, e.target.value)}
                                    className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-cream-dark bg-white focus:outline-none focus:ring-2 focus:ring-garden-green/30 text-deep-green text-sm cursor-pointer"
                                    data-testid={`takehome-select-${i}`}
                                >
                                    <option value={EMPTY}>— leer —</option>
                                    {flavors.map((f) => (
                                        <option key={f.id} value={f.name}>{f.name}</option>
                                    ))}
                                </select>
                            </div>
                            <button
                                onClick={() => removeSlot(i)}
                                className="p-2 rounded-lg hover:bg-red-100 text-red-700"
                                title="Platz entfernen"
                                data-testid={`takehome-remove-${i}`}
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>

                <AdminButton variant="secondary" onClick={addSlot} disabled={slots.length >= MAX_SLOTS} data-testid="takehome-add-slot">
                    <Plus className="w-4 h-4" /> Platz hinzufügen
                </AdminButton>
            </AdminCard>

            <AdminButton onClick={save} loading={saving} data-testid="take-home-save">
                <Save className="w-4 h-4" /> Speichern
            </AdminButton>
        </div>
    );
};

export default TakeHomeTab;
